import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { cn } from "@/lib/utils";
import type { SdrPerformance as SdrType } from "@/data/types";

export function SdrPerformance({ period }: { period: { periodStart: string; periodEnd: string } }) {
  const q = useQuery({
    queryKey: ["sdrs", period],
    queryFn: () => client.getSdrs(period),
  });

  const sdrs = q.data ?? [];
  const sorted = [...sdrs].sort((a, b) => b.appointments - a.appointments);
  const totalAppointments = sorted.reduce((a, s) => a + s.appointments, 0);

  return (
    <section className="rounded-lg border border-border-subtle bg-bg-card p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">Desempenho dos SDRs</h2>
          <p className="text-[13px] text-text-muted">Prospecção ativa · ordenado por agendamentos gerados</p>
        </div>
        <span className="text-xs text-text-muted">
          <span className="font-semibold text-brand-amber">{totalAppointments}</span> agendamentos no período
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {q.isLoading && (
          <div className="col-span-2 py-8 text-center text-sm text-text-muted">Carregando…</div>
        )}
        {sorted.map((s, idx) => (
          <SdrCard key={s.userId} sdr={s} rank={idx + 1} />
        ))}
      </div>
    </section>
  );
}

function SdrCard({ sdr, rank }: { sdr: SdrType; rank: number }) {
  return (
    <div className="rounded-md border border-border-subtle bg-bg-primary p-4 space-y-4 transition hover:border-border-strong">
      <div className="flex items-center gap-3">
        <span
          className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-text-on-accent"
          style={{ backgroundColor: sdr.avatarColor }}
        >
          {getInitials(sdr.name)}
        </span>
        <div className="flex-1">
          <div className="font-medium text-text-primary">{sdr.name}</div>
          <div className="text-xs text-text-muted">
            Ranking #{rank} · conexão {sdr.connectionRate.toFixed(1).replace(".", ",")}%
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 text-center">
        <Stat label="Leads" value={sdr.leadsHandled} accent="text-text-primary" />
        <Stat label="Ligações" value={sdr.calls} accent="text-text-secondary" />
        <Stat label="Conexões" value={sdr.connections} accent="text-chart-3" />
        <Stat label="Agendas" value={sdr.appointments} accent="text-brand-amber" />
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className="rounded border border-border-subtle bg-bg-card px-2 py-2">
      <div className={cn("text-lg font-bold tracking-tight", accent)}>{value}</div>
      <div className="text-[10px] font-medium uppercase tracking-wider text-text-muted">{label}</div>
    </div>
  );
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
